import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import AttachmentPanel from "./finance/AttachmentPanel";
import BarcodeScanner from "../components/hris/BarcodeScanner";
import KartuStok from "./KartuStok";
// Penyesuaian unit ber-IMEI rusak/hilang/pulih (brief 4 Sep 2026 P14) — panel
// mandiri, disisipkan satu baris di tab Unit di bawah.
import PenyesuaianUnitPanel from "../components/inventory/PenyesuaianUnitPanel";
import {
  Warehouse, Boxes, Smartphone, ArrowLeftRight, Scale, Plus, X, Download,
  Search, AlertTriangle, CheckCircle2, Trash2, Printer, Send, PackageCheck, Ban, BookOpen, Paperclip,
  ClipboardList } from "lucide-react";
import FakturPrint from "../components/faktur/FakturPrint";
import PemilihTemplate from "../components/faktur/PemilihTemplate";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO, hariIniDate, isoDariDate } from "@/lib/tanggal";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi,
} from "@/components/ui/responsive-table";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";
// Kategori dari backend datang sebagai KUNCI INTERNAL ("handphone_baru"),
// bukan label. Tanpa ini staf melihat "handphone_baru" di layar — sudah
// terjadi dan dilaporkan owner 24 Agu 2026 dari halaman ini.
import { labelKategori } from "@/lib/kategoriBarang";
import { useCabangOptions } from "@/lib/cabang";

const TAB = [
  { k: "stok", l: "Posisi Stok", i: Boxes },
  // Kartu Stok (brief owner 19 Agu poin 4): dulu menu tersendiri di sidebar, digabung
  // ke sini karena "masih berkaitan erat" — duduk tepat sesudah Posisi Stok karena
  // sama-sama menjawab pertanyaan "berapa/kenapa stok segini", bedanya cuma ringkasan
  // vs kronologi per SKU.
  { k: "kartu-stok", l: "Kartu Stok", i: BookOpen },
  { k: "gudang", l: "Gudang", i: Warehouse },
  { k: "unit", l: "Unit & Riwayat IMEI", i: Smartphone },
  { k: "transfer", l: "Transfer", i: ArrowLeftRight },
  { k: "cutover", l: "Saldo Awal", i: Scale },
  // P11/P12 (brief 4 Sep): kertas kerja opname mingguan. Duduk di ekor karena
  // ia alat CETAK, bukan layar pantau seperti tab-tab di atasnya.
  { k: "lembar", l: "Lembar Perhitungan", i: ClipboardList },
];

const hariIni = () => hariIniISO();
const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

// Alur bertahap transfer (brief owner 14 Agu poin 6): disiapkan -> dikirim ->
// diterima, atau dibatalkan sebelum diterima. Transfer LAMA (tanpa field
// `status`) ditandai backend sebagai "diterima" + `status_legacy: true` — fallback
// di sini (STATUS_TRANSFER.diterima) menjaga tampilan tetap normal kalau field
// itu entah kenapa belum terisi.
const STATUS_TRANSFER = {
  disiapkan: { l: "Disiapkan", c: "bg-amber-50 text-amber-700 border-amber-200" },
  dikirim: { l: "Dikirim", c: "bg-sky-50 text-sky-700 border-sky-200" },
  diterima: { l: "Diterima", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dibatalkan: { l: "Dibatalkan", c: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function InventoryStok() {
  const { can } = useAuth();
  // Halaman ini menggabungkan EMPAT modul backend yang izinnya terpisah, sementara menunya
  // cuma digerbangi inventory_laporan. Tanpa penjagaan per-bagian, pemegang role kustom
  // membuka halaman ini lalu langsung kena 403 di tiap request → layar kosong + toast merah.
  const bolehLaporan = can("inventory_laporan", "view");   // /inventory/laporan/* + /inventory/cutover/*
  const bolehGudang = can("inventory_gudang", "view");     // /inventory/warehouses
  const bolehUnit = can("inventory_unit", "view");         // /inventory/units
  const bolehTransfer = can("inventory_transfer", "view"); // /inventory/transfer
  // Kirim/Terima/Batalkan (tahap lanjutan setelah dibuat) — izin "update", sama
  // kunci modul dengan `require_permission("inventory_transfer","update")` di backend.
  const bolehTransferUpdate = can("inventory_transfer", "update");
  // Daftar template cetak (menu Template Faktur & Struk) memakai izin modul
  // "faktur" — terpisah dari modul-modul inventory di atas. Tanpa izin ini,
  // pemilih template dilewati dan backend memilih template default seperti
  // biasa (perilaku lama, tetap jalan).
  const bolehFaktur = can("faktur", "view");
  // Kartu Stok butuh izin yang SAMA dengan KartuStok.jsx sendiri (inventory_laporan) —
  // gabung tab tidak boleh berarti gabung gerbang izin jadi lebih longgar.
  // Lembar perhitungan memakai gerbang yang SAMA dengan endpoint-nya
  // (`inventory_laporan.view`) — tombolnya tidak boleh muncul untuk yang
  // ujungnya 403 (CLAUDE.md #3).
  const izinTab = { stok: bolehLaporan, "kartu-stok": bolehLaporan, gudang: bolehGudang, unit: bolehUnit, transfer: bolehTransfer, cutover: bolehLaporan, lembar: bolehLaporan };
  const tabTampil = TAB.filter(t => izinTab[t.k]);

  const [searchParams, setSearchParams] = useSearchParams();
  // Baca `?tab=` supaya tautan lama `/inventory/kartu-stok?sku=...&warehouse_id=...`
  // (dialihkan di App.js) dan link "Kartu Stok" di tabel Posisi Stok di bawah
  // mendarat di tab yang benar, bukan selalu jatuh ke tab default "Posisi Stok" —
  // pola sama persis dengan Retur.jsx/FinanceUtang.jsx.
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(tabTampil.some(t => t.k === urlTab) ? urlTab : (tabTampil[0]?.k || "stok"));
  const [gudang, setGudang] = useState([]);
  const [stok, setStok] = useState(null);
  const [units, setUnits] = useState({ items: [], per_status: {} });
  const [transfers, setTransfers] = useState([]);
  // Bukti fisik kirim/terima transfer (brief owner 29 Agu, Inven poin 2).
  const [lampiranTransfer, setLampiranTransfer] = useState(null);
  const [rekon, setRekon] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [riwayat, setRiwayat] = useState(null);
  // Filter tab "Unit & Riwayat IMEI" (brief bos 12 Agu poin 1 & 2): nama
  // produk/IMEI + periode tanggal + saklar "terjual saja". `periodeField`
  // menentukan tanggal MANA yang disaring oleh dari/sampai — lihat penjelasan
  // di backend (`_query_units`, stock.py) dan teks bantuan di bawah select-nya.
  // `status` (brief 4 Sep 2026 gugus D): chip per status di bawah filter jadi
  // saklar, supaya unit berstatus "hilang" bisa dipanggil ke layar dan diajukan
  // "ditemukan" lewat panel penyesuaian. Disaring di BACKEND (`list_units`
  // sudah menerima `status`), bukan memotong array di browser — daftarnya
  // dibatasi server, jadi menyaring di layar akan berbohong untuk baris yang
  // sudah terpotong.
  const [filterUnit, setFilterUnit] = useState({
    q: "", dari: "", sampai: "", periodeField: "masuk", hanyaTerjual: false, status: "",
  });
  const [unitBusy, setUnitBusy] = useState(false);
  // Filter tab "Posisi Stok" (brief owner 19 Agu poin 3): cari nama produk/SKU
  // + saring gudang/kategori/brand — disaring di BACKEND (bukan array di
  // browser), lihat `laporan_stok` di stock.py. `opsi` (daftar kategori/brand
  // yang bisa dipilih) datang dari respons endpoint yang sama, supaya tidak
  // perlu izin modul tambahan hanya untuk mengisi dua dropdown ini.
  const [filterStok, setFilterStok] = useState({
    q: "", warehouseId: "", kategori: "", brand: "", subKategori: "", supplier: "",
  });
  const [stokOpsi, setStokOpsi] = useState({ kategori: [], brand: [], sub_kategori: [] });
  const [supplierOpsi, setSupplierOpsi] = useState([]);
  const [stokBusy, setStokBusy] = useState(false);
  // Pengurutan tabel Posisi Stok — CLIENT-SIDE atas hasil yang SUDAH tersaring
  // backend (lihat components/ui/sort.jsx), sesuai permintaan "sort by
  // brand/gudang/kategori/nama".
  const sortStok = useSortState(null, "asc");
  const [formGudang, setFormGudang] = useState(null);
  const [formTransfer, setFormTransfer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // {transfer, step, templates, templateId, loading, data} — brief #18 cetak
  // surat jalan + pemilih template. step: "memuat_template" | "pilih_template" |
  // "kosong" | "preview".
  const [cetakTransfer, setCetakTransfer] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      // Hanya panggil endpoint yang izinnya dipegang user — memanggil lalu gagal 403 membuat
      // Promise.all menolak dan SELURUH halaman kosong, termasuk bagian yang sebenarnya boleh.
      const [g, s, u, t] = await Promise.all([
        bolehGudang ? api.get("/inventory/warehouses", { params: { with_stock: true } }) : null,
        bolehLaporan ? api.get("/inventory/laporan/stok") : null,
        bolehUnit ? api.get("/inventory/units") : null,
        bolehTransfer ? api.get("/inventory/transfer") : null,
      ]);
      if (g) setGudang(g.data.items || []);
      if (s) { setStok(s.data); setStokOpsi(s.data.opsi || { kategori: [], brand: [] }); }
      if (u) setUnits(u.data);
      if (t) setTransfers(t.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data stok");
    } finally { setLoading(false); }
  };

  const loadCutover = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get("/inventory/cutover/rekonsiliasi"),
        api.get("/inventory/cutover/config"),
      ]);
      setRekon(r.data);
      setCfg(c.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat rekonsiliasi"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // Endpoint cutover memakai izin inventory_laporan — jangan dipanggil kalau tidak dipegang.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "cutover" && bolehLaporan) loadCutover(); }, [tab]);

  // Ganti tab lewat klik: dorong balik ke URL supaya tombol Back browser dan
  // tautan yang dibagikan/di-bookmark tetap mendarat di tab yang sama (pola sama
  // persis dengan `selectTab` di Retur.jsx / `selectFilter` di FinanceUtang.jsx).
  const selectTab = (key) => {
    setTab(key);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", key);
      return next;
    }, { replace: true });
  };

  // Sinkron ulang kalau URL berubah SETELAH halaman ini sudah ter-mount — mis. klik
  // link "Kartu Stok" di tabel Posisi Stok, atau redirect dari rute lama
  // `/inventory/kartu-stok` sementara halaman ini masih ter-mount di riwayat browser.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && tabTampil.some(x => x.k === t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const hapusGudang = async (g) => {
    // Nonaktifkan, bukan hapus permanen — kartu stok & riwayat lama tetap
    // menunjuk gudang ini. Backend menolak kalau stoknya belum kosong.
    if (!window.confirm(`Nonaktifkan gudang "${g.nama}" (${g.code})?\n\nGudang harus kosong dulu — riwayat lama tetap tersimpan.`)) return;
    await kirim(() => api.delete(`/inventory/warehouses/${g.id}`), "Gudang dinonaktifkan");
  };

  const kirim = async (fn, pesan) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || pesan);
      await load();
      if (tab === "cutover") await loadCutover();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses");
      return false;
    } finally { setBusy(false); }
  };

  const lihatRiwayat = async (ident) => {
    try {
      const r = await api.get(`/inventory/units/${encodeURIComponent(ident)}/riwayat`);
      setRiwayat(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Unit tidak ditemukan"); }
  };

  // Parameter filter unit → dipakai bersama oleh pencarian layar (cariUnits)
  // dan ekspor Excel (exportUnits), supaya keduanya SELALU menyaring hal yang
  // sama persis — tidak ada file Excel yang isinya beda dari yang terlihat di layar.
  const paramFilterUnit = () => {
    const p = {};
    if (filterUnit.q.trim()) p.q = filterUnit.q.trim();
    if (filterUnit.dari) p.date_from = filterUnit.dari;
    if (filterUnit.sampai) p.date_to = filterUnit.sampai;
    if (filterUnit.dari || filterUnit.sampai) p.periode_field = filterUnit.periodeField;
    // Chip status MENANG atas saklar "Terjual saja": chip dipilih dengan sengaja
    // satu klik sebelumnya, jadi ia yang paling mencerminkan maksud terakhir.
    if (filterUnit.status) p.status = filterUnit.status;
    else if (filterUnit.hanyaTerjual) p.status = "terjual";
    return p;
  };

  const cariUnits = async () => {
    if (!bolehUnit) return;
    setUnitBusy(true);
    try {
      const r = await api.get("/inventory/units", { params: paramFilterUnit() });
      setUnits(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar unit");
    } finally { setUnitBusy(false); }
  };

  // Chip status: klik = saring ke status itu, klik lagi = lepas. Pemuatannya
  // dilakukan di sini (bukan menunggu tombol Terapkan) karena satu klik chip
  // sudah merupakan perintah yang lengkap.
  const pilihStatusUnit = async (s) => {
    const baru = filterUnit.status === s ? "" : s;
    setFilterUnit(f => ({ ...f, status: baru }));
    if (!bolehUnit) return;
    setUnitBusy(true);
    try {
      const p = paramFilterUnit();
      if (baru) p.status = baru; else if (filterUnit.hanyaTerjual) p.status = "terjual"; else delete p.status;
      const r = await api.get("/inventory/units", { params: p });
      setUnits(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar unit");
    } finally { setUnitBusy(false); }
  };

  const resetFilterUnit = async () => {
    setFilterUnit({ q: "", dari: "", sampai: "", periodeField: "masuk", hanyaTerjual: false, status: "" });
    setUnitBusy(true);
    try {
      const r = await api.get("/inventory/units");
      setUnits(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar unit");
    } finally { setUnitBusy(false); }
  };

  // Parameter filter Posisi Stok — dikirim ke `/inventory/laporan/stok`
  // (penyaringan di backend, lihat catatan di stock.py::laporan_stok).
  const paramFilterStok = () => {
    const p = {};
    if (filterStok.q.trim()) p.q = filterStok.q.trim();
    if (filterStok.warehouseId) p.warehouse_id = filterStok.warehouseId;
    if (filterStok.kategori) p.kategori = filterStok.kategori;
    if (filterStok.brand) p.brand = filterStok.brand;
    if (filterStok.subKategori) p.sub_kategori = filterStok.subKategori;
    if (filterStok.supplier) p.supplier = filterStok.supplier;
    return p;
  };

  const cariStok = async () => {
    if (!bolehLaporan) return;
    setStokBusy(true);
    try {
      const r = await api.get("/inventory/laporan/stok", { params: paramFilterStok() });
      setStok(r.data);
      setStokOpsi(r.data.opsi || { kategori: [], brand: [], sub_kategori: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat posisi stok");
    } finally { setStokBusy(false); }
  };

  // Daftar supplier dimuat terpisah — sama seperti di Master Produk. Kalau
  // gagal, filter supplier tidak ditampilkan dan halaman tetap jalan.
  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        // Endpoint & bentuk respons SAMA dengan Master Produk (`/supplier`,
        // `data.items`) — bukan endpoint lain yang kebetulan mirip.
        const r = await api.get("/supplier");
        if (!batal) setSupplierOpsi(r.data?.items || []);
      } catch { /* filter supplier disembunyikan */ }
    })();
    return () => { batal = true; };
  }, []);

  const resetFilterStok = async () => {
    setFilterStok({ q: "", warehouseId: "", kategori: "", brand: "", subKategori: "", supplier: "" });
    setStokBusy(true);
    try {
      const r = await api.get("/inventory/laporan/stok");
      setStok(r.data);
      setStokOpsi(r.data.opsi || { kategori: [], brand: [], sub_kategori: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat posisi stok");
    } finally { setStokBusy(false); }
  };

  // Riwayat/arus barang per SKU × gudang (brief owner 19 Agu poin 3) dibuka di
  // TAB BROWSER BARU, bukan tab dalam halaman — dipilih karena tab ini dipakai
  // SAMBIL menyisir daftar Posisi Stok yang sudah disaring: membuka riwayat di
  // tab yang sama akan membuang hasil pencarian/filter yang sedang aktif.
  // Kartu Stok sendiri sudah jadi tab dalam halaman ini (`tab=kartu-stok`)
  // untuk kasus lain (klik dari menu/URL langsung) — di sini kita sengaja
  // memaksanya jadi jendela terpisah lewat `window.open`.
  const bukaKartuStokBaru = (sku, warehouseId) => {
    const qs = new URLSearchParams({ tab: "kartu-stok", sku });
    if (warehouseId) qs.set("warehouse_id", warehouseId);
    window.open(`/inventory/stok?${qs.toString()}`, "_blank", "noopener,noreferrer");
  };

  const exportUnits = async () => {
    try {
      const token = localStorage.getItem("hris_token");
      const qs = new URLSearchParams(paramFilterUnit()).toString();
      const res = await fetch(`${API}/inventory/units/export${qs ? `?${qs}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = `Unit_IMEI_${filterUnit.dari || "semua"}_${filterUnit.sampai || "semua"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch { toast.error("Gagal mengunduh rekap unit"); }
  };

  // Tahap lanjutan transfer (brief owner 14 Agu poin 6) — 'kirim'/'terima' tidak
  // butuh input tambahan, 'batalkan' wajib alasan (dicek juga di backend).
  const kirimTransfer = (t) => kirim(() => api.post(`/inventory/transfer/${t.id}/kirim`, {}),
    `Transfer ${t.code} ditandai dikirim`);
  const terimaTransfer = (t) => kirim(() => api.post(`/inventory/transfer/${t.id}/terima`, {}),
    `Transfer ${t.code} diterima — stok masuk ke gudang tujuan`);
  const batalkanTransfer = (t) => {
    const alasan = window.prompt(`Alasan pembatalan transfer ${t.code}:`, "");
    if (alasan === null) return;               // batal di dialog prompt
    if (!alasan.trim()) { toast.error("Alasan pembatalan wajib diisi"); return; }
    kirim(() => api.post(`/inventory/transfer/${t.id}/batalkan`, { alasan: alasan.trim() }),
      `Transfer ${t.code} dibatalkan`);
  };

  // Cetak Surat Jalan pindah gudang (brief #18) — data BACA-SAJA, template
  // "pindah_gudang" punya blok TTD pembuat/disetujui/diterima (brief B.7).
  // Kalau jenis ini punya lebih dari satu template, pengguna memilih dulu.
  const bukaCetakTransfer = async (t) => {
    setCetakTransfer({ transfer: t, step: "memuat_template", templates: [], templateId: null, loading: true, data: null });
    if (!bolehFaktur) { await muatDokumenTransfer(t, null); return; }
    try {
      // warehouse_id (brief bos): pembatasan gudang TEMPLATE dicek terhadap
      // gudang ASAL (dari_warehouse_id) — konsisten dengan resolusi backend
      // di `data_faktur_pindah_gudang` (faktur.py), yang juga memakai gudang
      // asal sebagai dasar cabang & pembatasan gudang dokumen ini.
      const { data } = await api.get("/faktur/templates", {
        params: { jenis: "pindah_gudang", warehouse_id: t.dari_warehouse_id || undefined },
      });
      const daftar = data?.items || [];
      if (daftar.length === 0) {
        setCetakTransfer({ transfer: t, step: "kosong", templates: [], loading: false, data: null });
        return;
      }
      if (daftar.length === 1) { await muatDokumenTransfer(t, daftar[0]); return; }
      const def = daftar.find(x => x.is_default) || daftar[0];
      setCetakTransfer({ transfer: t, step: "pilih_template", templates: daftar, templateId: def.id, loading: false, data: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar template cetak");
      setCetakTransfer(null);
    }
  };

  const muatDokumenTransfer = async (t, template) => {
    setCetakTransfer(m => ({ ...(m || { transfer: t }), transfer: t, step: "preview", loading: true, data: null }));
    try {
      const { data } = await api.get(`/faktur/data/pindah-gudang/${t.id}`, {
        params: template ? { template_id: template.id } : {},
      });
      setCetakTransfer(m => (m ? { ...m, step: "preview", loading: false, data } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat surat jalan");
      setCetakTransfer(null);
    }
  };

  const exportLaporan = async () => {
    try {
      const token = localStorage.getItem("hris_token");
      const tglDari = hariIniDate(); tglDari.setDate(tglDari.getDate() - 30);
      const dari = isoDariDate(tglDari);
      const res = await fetch(`${API}/inventory/laporan/export?date_from=${dari}&date_to=${hariIni()}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = `Laporan_Stok_${dari}_${hariIni()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch { toast.error("Gagal mengunduh laporan"); }
  };

  // Pengurutan Posisi Stok atas hasil yang SUDAH tersaring backend (lihat
  // `cariStok`) — field "gudang" perlu getter khusus karena namanya di data
  // asli `warehouse_nama`, bukan `gudang`.
  const itemsStokTampil = sortRows(stok?.items || [], sortStok.sortKey, sortStok.sortDir,
    { gudang: r => r.warehouse_nama });

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="inventory-stok">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Stok & Gudang</h1>
          <p className="text-sm text-slate-600 mt-1">
            Kuantitas dari kartu stok, nilai dari lapisan modal — keduanya bisa direkonsiliasi ke buku besar.
          </p>
        </div>
        {/* Export memakai /inventory/laporan/export (izin inventory_laporan) — sembunyikan
            tombolnya daripada membiarkan user menekan sesuatu yang pasti gagal. */}
        {bolehLaporan && (
          <button onClick={exportLaporan} data-testid="inv-stok-export"
                  className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Laporan Excel
          </button>
        )}
      </div>

      {tabTampil.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800"
             data-testid="inv-stok-noizin">
          Akun Anda belum diberi izin melihat bagian mana pun dari Stok &amp; Gudang. Minta admin
          mencentang modul Laporan Stok, Gudang, Unit, atau Transfer pada role Anda.
        </div>
      )}

      <div className="flex items-center gap-1 flex-wrap border-b border-slate-200 pb-2">
        {tabTampil.map(({ k, l, i: I }) => (
          <button key={k} onClick={() => selectTab(k)} data-testid={`inv-stok-tab-${k}`}
                  className={`px-3 h-9 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <I className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {loading && tabTampil.length > 0 && <LoadingState loading data-testid="inv-stok-loading" />}

      {!loading && tab === "stok" && stok && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Kartu label="Total Unit" nilai={stok.total_qty} sub={`${stok.count} baris SKU × gudang`} />
            <Kartu label="Nilai Persediaan" nilai={idr(stok.total_nilai)} sub="dari lapisan modal" />
            <Kartu label="Stok Menipis" nilai={stok.low_stock.length} sub="di bawah stok minimum"
                   tone={stok.low_stock.length ? "text-rose-700" : "text-slate-900"} />
            {/* Transparansi (brief owner 14 Agu poin 6): barang yang sudah 'disiapkan'/
                'dikirim' lenyap dari kartu stok gudang asal tapi belum muncul di gudang
                tujuan — tanpa kartu ini nilai persediaan terlihat menyusut tanpa sebab. */}
            {stok.dalam_perjalanan && (
              <Kartu label="Dalam Perjalanan" nilai={idr(stok.dalam_perjalanan.nilai)}
                     sub={`${stok.dalam_perjalanan.jumlah_transfer} transfer · ${stok.dalam_perjalanan.qty} unit`}
                     tone={stok.dalam_perjalanan.jumlah_transfer ? "text-amber-700" : "text-slate-900"} />
            )}
          </div>

          {/* Pencarian & filter (brief owner 19 Agu poin 3) — disaring di
              BACKEND lewat `/inventory/laporan/stok` (`q`/`warehouse_id`/
              `kategori`/`brand`), bukan array yang sudah terunduh penuh di
              browser. Baris ini TIDAK dibungkus TabelDesktop/DaftarKartuMobile
              jadi ikut tampil (dan tetap berfungsi) di layar HP. */}
          {/* Susunan & urutan filter DISAMAKAN dengan Master Produk (permintaan
              owner 26 Agu 2026): cari -> kategori -> kat. custom -> brand ->
              gudang -> supplier, satu baris ringkas tanpa label di atas tiap
              kotak. Alasannya bukan estetika: pengguna yang sama berpindah
              antara dua layar ini setiap hari, dan urutan yang berbeda membuat
              mereka salah pilih kotak. Dropdown yang datanya kosong sengaja
              tidak dirender - persis perilaku Master Produk. */}
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-3">
            {/* Filter DIKELOMPOKKAN (brief owner 26 Agu, Stok & Gudang):
                sebelumnya tujuh kontrol berjajar datar tanpa keterangan, dan
                pengguna harus menebak mana yang menyaring BARANG dan mana yang
                menyaring TEMPAT. Isinya persis sama — yang berubah hanya
                pengelompokan dan labelnya. */}
            <div className="flex items-start gap-4 flex-wrap">
             <div className="flex flex-col gap-1" data-testid="inv-stok-grup-barang">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Barang</span>
              <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={filterStok.q} onChange={e => setFilterStok(f => ({ ...f, q: e.target.value }))}
                       onKeyDown={e => e.key === "Enter" && cariStok()}
                       placeholder="Cari SKU / nama produk" data-testid="inv-stok-cari"
                       aria-label="Cari SKU atau nama produk"
                       className="h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm w-60" />
              </div>
              <select value={filterStok.kategori} data-testid="inv-stok-filter-kategori"
                      aria-label="Filter kategori produk"
                      onChange={e => setFilterStok(f => ({ ...f, kategori: e.target.value }))}
                      className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
                <option value="">Semua kategori</option>
                {stokOpsi.kategori.map(k => <option key={k} value={k}>{labelKategori(k)}</option>)}
              </select>
              {(stokOpsi.sub_kategori || []).length > 0 && (
                <select value={filterStok.subKategori} data-testid="inv-stok-filter-subkategori"
                        aria-label="Filter kategori custom"
                        onChange={e => setFilterStok(f => ({ ...f, subKategori: e.target.value }))}
                        className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Semua kat. custom</option>
                  {stokOpsi.sub_kategori.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              )}
              {stokOpsi.brand.length > 0 && (
                <select value={filterStok.brand} data-testid="inv-stok-filter-brand"
                        aria-label="Filter brand"
                        onChange={e => setFilterStok(f => ({ ...f, brand: e.target.value }))}
                        className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Semua brand</option>
                  {stokOpsi.brand.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              )}
              </div>
             </div>
             <div className="flex flex-col gap-1" data-testid="inv-stok-grup-lokasi">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Lokasi &amp; Supplier</span>
              <div className="flex items-center gap-2 flex-wrap">
              {gudang.length > 0 && (
                <select value={filterStok.warehouseId} data-testid="inv-stok-filter-gudang"
                        aria-label="Filter gudang"
                        onChange={e => setFilterStok(f => ({ ...f, warehouseId: e.target.value }))}
                        className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Semua gudang</option>
                  {[...gudang].sort((a, b) => (a.nama || "").localeCompare(b.nama || "", "id"))
                    .map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
                </select>
              )}
              {supplierOpsi.length > 0 && (
                <select value={filterStok.supplier} data-testid="inv-stok-filter-supplier"
                        aria-label="Filter supplier"
                        onChange={e => setFilterStok(f => ({ ...f, supplier: e.target.value }))}
                        className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Semua supplier</option>
                  {[...supplierOpsi].sort((a, b) => (a.nama || "").localeCompare(b.nama || "", "id"))
                    .map(sp => <option key={sp.id} value={sp.nama}>{sp.nama}</option>)}
                </select>
              )}
              </div>
             </div>
             <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-transparent select-none">.</span>
              <div className="flex items-center gap-2 flex-wrap">
              <button onClick={cariStok} disabled={stokBusy} data-testid="inv-stok-terapkan"
                      className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {stokBusy ? "Memuat..." : "Terapkan Filter"}
              </button>
              <button onClick={resetFilterStok} disabled={stokBusy} data-testid="inv-stok-reset"
                      className="h-9 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
                Reset
              </button>
              </div>
             </div>
            </div>
            {stokOpsi.brand.length === 0 && (
              <p className="text-[11px] text-slate-400">
                Belum ada produk dengan brand terisi — filter Brand akan kosong sampai brand diisi di Master Barang.
              </p>
            )}
          </div>

          <Tabel kolom={[
                   { key: "sku", label: "SKU", testid: "inv-stok-sort-sku" },
                   { key: "nama", label: "Nama", testid: "inv-stok-sort-nama" },
                   { key: "brand", label: "Brand", testid: "inv-stok-sort-brand" },
                   { key: "kategori", label: "Kategori", testid: "inv-stok-sort-kategori" },
                   { key: "gudang", label: "Gudang", testid: "inv-stok-sort-gudang" },
                   { key: "qty", label: "Qty", testid: "inv-stok-sort-qty" },
                   { key: "nilai", label: "Nilai", testid: "inv-stok-sort-nilai" },
                 ]} kanan={[5, 6]}
                 sortKey={sortStok.sortKey} sortDir={sortStok.sortDir} onSort={sortStok.toggle}
                 kosong={filterStok.q || filterStok.warehouseId || filterStok.kategori || filterStok.brand
                             || filterStok.subKategori || filterStok.supplier
                   ? "Tidak ada stok yang cocok dengan filter ini."
                   : "Belum ada stok. Catat penerimaan barang dulu."}
                 kartuTestid="inv-stok-kartu"
                 baris={itemsStokTampil.map(i => ({
                   key: `${i.sku}-${i.warehouse_id}`,
                   testid: `inv-stok-${i.sku}`,
                   // Klik baris = buka riwayat/arus barang SKU ini di TAB BROWSER
                   // BARU (lihat penjelasan `bukaKartuStokBaru`) — supaya hasil
                   // pencarian/filter yang sedang dilihat tidak hilang.
                   onClick: () => bukaKartuStokBaru(i.sku, i.warehouse_id),
                   sel: [
                     <span>
                       <span className="font-mono text-xs block">{i.sku}</span>
                       {/* Tautan kecil per SKU (batch D #4) — buka kronologi mutasi lengkap
                           dengan saldo berjalan, gudang ini dipilih otomatis lewat query string.
                           stopPropagation supaya klik di sini tidak memicu DUA tab terbuka
                           sekaligus (link ini + onClick baris di atas). */}
                       <Link to={`/inventory/stok?tab=kartu-stok&sku=${encodeURIComponent(i.sku)}&warehouse_id=${encodeURIComponent(i.warehouse_id)}`}
                             target="_blank" rel="noopener noreferrer"
                             onClick={e => e.stopPropagation()}
                             data-testid={`inv-stok-kartu-${i.sku}`}
                             className="text-[10px] text-emerald-700 hover:underline">
                         Kartu Stok
                       </Link>
                     </span>,
                     <span>{i.nama}{i.low_stock && <AlertTriangle className="w-3 h-3 text-rose-600 inline ml-1" />}</span>,
                     i.brand || "—",
                     labelKategori(i.kategori),
                     i.warehouse_nama,
                     <span className={i.low_stock ? "text-rose-700 font-semibold" : ""}>{i.qty}</span>,
                     <span className="font-mono">{idr(i.nilai)}</span>,
                   ],
                 }))}
                 kartu={itemsStokTampil.map(i => (
                   <KartuBaris key={`${i.sku}-${i.warehouse_id}`} testid={`inv-stok-${i.sku}-m`}
                               onClick={() => bukaKartuStokBaru(i.sku, i.warehouse_id)}>
                     <KartuBarisHeader
                       kiri={<>
                         <p className="font-mono text-[11px] text-slate-500">{i.sku}</p>
                         <p className="text-sm font-semibold text-slate-900">
                           {i.nama}{i.low_stock && <AlertTriangle className="w-3 h-3 text-rose-600 inline ml-1" />}
                         </p>
                         <Link to={`/inventory/stok?tab=kartu-stok&sku=${encodeURIComponent(i.sku)}&warehouse_id=${encodeURIComponent(i.warehouse_id)}`}
                               target="_blank" rel="noopener noreferrer"
                               onClick={e => e.stopPropagation()}
                               data-testid={`inv-stok-kartu-${i.sku}-m`}
                               className="text-[10px] text-emerald-700 hover:underline">
                           Kartu Stok
                         </Link>
                       </>}
                       kanan={<span className={`font-mono ${i.low_stock ? "text-rose-700 font-semibold" : "text-slate-800"}`}>{i.qty}</span>}
                     />
                     <KartuBarisMeta label="Brand" value={i.brand} />
                     <KartuBarisMeta label="Kategori" value={labelKategori(i.kategori)} />
                     <KartuBarisMeta label="Gudang" value={i.warehouse_nama} />
                     <KartuBarisMeta label="Nilai" value={idr(i.nilai)} tebal />
                   </KartuBaris>
                 ))} />
        </div>
      )}

      {/* KartuStok.jsx sudah mandiri — izin sendiri, baca `sku`/`warehouse_id` dari
          query string sendiri — dipakai apa adanya di sini (bukan disalin ulang)
          supaya satu-satunya sumber kebenaran kartu stok tetap satu berkas. Hanya
          dirender saat tab ini aktif, jadi fetch-nya tidak ikut menembak API saat
          tab lain yang sedang dibuka (brief owner 19 Agu poin 4). */}
      {!loading && tab === "kartu-stok" && <KartuStok />}

      {!loading && tab === "gudang" && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Daftar gudang / toko ini dipakai langsung oleh Inventory. Etalase / rak diatur di dalam masing-masing tempat.</p>
          <Link to="/inventory?tab=input" className="inline-block text-sm font-semibold text-blue-600">Kembali ke Input Inventory</Link>
          {can("inventory_gudang", "create") && (
            <button onClick={() => setFormGudang({
              nama: "", tipe: "gudang", cabang: "", all_branches: true,
              allowed_branches: [], alamat: "", catatan: "", is_active: true, is_utama: false,
            })} data-testid="inv-gudang-add"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Gudang Baru
            </button>
          )}
          <Tabel kolom={["Kode", "Nama", "Tipe", "Akses Cabang", "SKU", "Unit Tersedia", "Total Qty", "Aksi"]}
                 kanan={[4, 5, 6, 7]}
                 kosong="Belum ada gudang. Barang tidak bisa diterima tanpa tempat menyimpannya."
                 kartuTestid="inv-gudang-kartu"
                 baris={gudang.map(g => ({
                   key: g.id, testid: `inv-gudang-${g.id}`,
                   sel: [
                     <span className="font-mono text-xs">{g.code}</span>,
                     <span>{g.nama}{g.is_utama && <span data-testid={`inv-gudang-utama-${g.id}`} className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold align-middle">Utama</span>}</span>,
                     g.tipe === "toko" ? "Toko" : "Gudang",
                     g.all_branches
                       ? <span className="text-emerald-700 text-xs">Semua cabang</span>
                       : <span className="text-xs">{(g.allowed_branches || []).join(", ")}</span>,
                     g.jumlah_sku ?? 0, g.unit_tersedia ?? 0, g.total_qty ?? 0,
                     <div className="flex items-center justify-end gap-1">
                       <Link to={`/inventory?tab=input&panel=lokasi&warehouse=${encodeURIComponent(g.id)}`} data-testid={`inv-gudang-lokasi-${g.id}`} className="px-2 py-1 text-blue-600 text-xs">Etalase / rak</Link>
                       {can("inventory_gudang", "update") && (
                         <button onClick={() => setFormGudang({
                                   // salin field yang memang milik form — bukan seluruh
                                   // dokumen berikut angka statistiknya
                                   id: g.id, nama: g.nama, code: g.code, tipe: g.tipe,
                                   cabang: g.cabang || "", all_branches: !!g.all_branches,
                                   allowed_branches: g.allowed_branches || [],
                                   alamat: g.alamat || "", catatan: g.catatan || "",
                                   is_active: g.is_active !== false, is_utama: !!g.is_utama,
                                 })}
                                 data-testid={`inv-gudang-edit-${g.id}`}
                                 className="px-2 h-7 rounded hover:bg-slate-100 text-slate-600 text-xs">Ubah</button>
                       )}
                       {can("inventory_gudang", "delete") && (
                         <button onClick={() => hapusGudang(g)} data-testid={`inv-gudang-del-${g.id}`}
                                 title="Nonaktifkan gudang (stok harus kosong dulu)"
                                 className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                           <Trash2 className="w-3.5 h-3.5" />
                         </button>
                       )}
                     </div>,
                   ],
                 }))}
                 kartu={gudang.map(g => (
                   <KartuBaris key={g.id} testid={`inv-gudang-${g.id}-m`}>
                     <KartuBarisHeader
                       kiri={<>
                         <p className="font-mono text-[11px] text-slate-500">{g.code}</p>
                         <p className="text-sm font-semibold text-slate-900">{g.nama}{g.is_utama && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold align-middle">Utama</span>}</p>
                         <p className="text-xs text-slate-500 mt-0.5">{g.tipe === "toko" ? "Toko" : "Gudang"}</p>
                       </>}
                       kanan={g.all_branches
                         ? <span className="text-emerald-700 text-xs">Semua cabang</span>
                         : <span className="text-xs text-right">{(g.allowed_branches || []).join(", ") || "—"}</span>}
                     />
                     <KartuBarisMeta label="SKU" value={g.jumlah_sku ?? 0} />
                     <KartuBarisMeta label="Unit Tersedia" value={g.unit_tersedia ?? 0} />
                     <KartuBarisMeta label="Total Qty" value={g.total_qty ?? 0} tebal />
                     <KartuBarisAksi>
                       <Link to={`/inventory?tab=input&panel=lokasi&warehouse=${encodeURIComponent(g.id)}`} data-testid={`inv-gudang-lokasi-${g.id}-m`} className="px-2 py-1 text-blue-600 text-xs">Etalase / rak</Link>
                       {can("inventory_gudang", "update") && (
                         <button onClick={() => setFormGudang({
                                   id: g.id, nama: g.nama, code: g.code, tipe: g.tipe,
                                   cabang: g.cabang || "", all_branches: !!g.all_branches,
                                   allowed_branches: g.allowed_branches || [],
                                   alamat: g.alamat || "", catatan: g.catatan || "",
                                   is_active: g.is_active !== false, is_utama: !!g.is_utama,
                                 })}
                                 data-testid={`inv-gudang-edit-${g.id}-m`}
                                 className="px-2.5 h-8 rounded hover:bg-slate-100 text-slate-600 text-xs font-medium">Ubah</button>
                       )}
                       {can("inventory_gudang", "delete") && (
                         <button onClick={() => hapusGudang(g)} data-testid={`inv-gudang-del-${g.id}-m`}
                                 title="Nonaktifkan gudang (stok harus kosong dulu)"
                                 className="px-2.5 h-8 rounded hover:bg-rose-50 text-rose-600 text-xs font-medium inline-flex items-center gap-1">
                           <Trash2 className="w-3.5 h-3.5" /> Nonaktifkan
                         </button>
                       )}
                     </KartuBarisAksi>
                   </KartuBaris>
                 ))} />
        </div>
      )}

      {!loading && tab === "unit" && (
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={filterUnit.q} onChange={e => setFilterUnit(f => ({ ...f, q: e.target.value }))}
                       onKeyDown={e => e.key === "Enter" && cariUnits()}
                       placeholder="Cari nama produk (mis. iphone 15 128gb black) atau IMEI/SN..."
                       data-testid="inv-unit-cari" className="h-10 pl-9 pr-3 border border-slate-200 rounded-lg text-sm w-80 md:w-[26rem]" />
              </div>
              <BarcodeScanner onDetected={lihatRiwayat} testId="inv-unit-scan" title="Scan IMEI untuk lihat riwayat" />
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Dari tanggal</label>
                <DatePicker value={filterUnit.dari} data-testid="inv-unit-dari"
                            onChange={v => setFilterUnit(f => ({ ...f, dari: v }))} className="w-40" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Sampai tanggal</label>
                <DatePicker value={filterUnit.sampai} data-testid="inv-unit-sampai"
                            onChange={v => setFilterUnit(f => ({ ...f, sampai: v }))} className="w-40" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Periode berdasarkan</label>
                <select value={filterUnit.periodeField} data-testid="inv-unit-periode-field"
                        onChange={e => setFilterUnit(f => ({ ...f, periodeField: e.target.value }))}
                        className="h-10 px-3 border border-slate-200 rounded-lg text-sm">
                  <option value="masuk">Tanggal Masuk</option>
                  <option value="terjual">Tanggal Terjual</option>
                </select>
              </div>
              <label className="h-10 px-3 inline-flex items-center gap-2 text-sm text-slate-700 border border-slate-200 rounded-lg cursor-pointer bg-white">
                <input type="checkbox" checked={filterUnit.hanyaTerjual} data-testid="inv-unit-hanya-terjual"
                       onChange={e => setFilterUnit(f => ({ ...f, hanyaTerjual: e.target.checked }))}
                       className="w-4 h-4 rounded border-slate-300" />
                Terjual saja
              </label>
              <button onClick={cariUnits} disabled={unitBusy} data-testid="inv-unit-terapkan"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {unitBusy ? "Memuat..." : "Terapkan Filter"}
              </button>
              <button onClick={resetFilterUnit} disabled={unitBusy} data-testid="inv-unit-reset"
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
                Reset
              </button>
              <div className="flex-1" />
              <button onClick={exportUnits} data-testid="inv-unit-export"
                      className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
                <Download className="w-4 h-4" /> Export Excel
              </button>
            </div>
            <p className="text-[11px] text-slate-500" data-testid="inv-unit-periode-help">
              {filterUnit.periodeField === "masuk"
                ? "Periode di atas menyaring tanggal UNIT MASUK ke gudang — unit yang sudah terjual tetap tampil (ditandai merah) selama tanggal masuknya ada di rentang ini."
                : "Periode di atas menyaring tanggal PENJUALAN — hanya unit yang benar-benar laku di rentang ini yang tampil."}
              {units.modal_terlihat === false && " Kolom Harga Jual & Modal disembunyikan — akun Anda tidak punya izin Laporan Stok."}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Status yang SEDANG dipakai menyaring selalu ikut dirender, walau
                  hitungannya jatuh ke nol (mis. unit hilang terakhir baru saja
                  "ditemukan") — kalau tidak, chipnya lenyap dan saringannya
                  tidak bisa dilepas lagi selain lewat tombol Reset. */}
              {Array.from(new Set([
                ...Object.keys(units.per_status || {}),
                ...(filterUnit.status ? [filterUnit.status] : []),
              ])).map(s => (
                <button key={s} type="button" onClick={() => pilihStatusUnit(s)} disabled={unitBusy}
                        data-testid={`inv-unit-chip-${s}`}
                        title={filterUnit.status === s ? "Klik lagi untuk melepas saringan status" : `Tampilkan hanya unit berstatus ${s}`}
                        className={`text-xs px-2 py-1 rounded border disabled:opacity-60 ${
                          filterUnit.status === s
                            ? "bg-emerald-600 border-emerald-600 text-white"
                            : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"}`}>
                  {s}: <strong>{(units.per_status || {})[s]?.jumlah ?? 0}</strong>
                </button>
              ))}
              {filterUnit.status && (
                <span className="text-[11px] text-slate-500">
                  disaring ke status "{filterUnit.status}" — klik chip yang sama untuk melepas
                </span>
              )}
            </div>
          </div>
          <PenyesuaianUnitPanel units={units.items} modalTerlihat={!!units.modal_terlihat} onSelesai={cariUnits} />
          <Tabel kolom={units.modal_terlihat
                   ? ["IMEI / SN", "Produk", "Gudang", "Status", "Masuk", "Terjual", "Harga Jual", "Modal"]
                   : ["IMEI / SN", "Produk", "Gudang", "Status", "Masuk", "Terjual"]}
                 kanan={units.modal_terlihat ? [6, 7] : []}
                 kosong="Belum ada unit ber-IMEI tercatat untuk filter ini."
                 kartuTestid="inv-unit-kartu"
                 baris={units.items.map(u => ({
                   key: u.id, testid: `inv-unit-${u.identifier}`,
                   onClick: () => lihatRiwayat(u.identifier),
                   kelas: u.status === "terjual" ? "bg-rose-50/70" : "",
                   sel: [
                     <span className="font-mono text-xs text-emerald-800">{u.identifier}</span>,
                     u.nama,
                     u.warehouse_nama,
                     <StatusUnitBadge status={u.status} />,
                     u.tanggal_masuk,
                     u.status === "terjual" ? (u.tanggal_terjual || "—") : "—",
                     ...(units.modal_terlihat ? [
                       <span className="font-mono">{u.status === "terjual" ? idr(u.harga_jual) : "—"}</span>,
                       <span className="font-mono">{idr(u.harga_perolehan)}</span>,
                     ] : []),
                   ],
                 }))}
                 kartu={units.items.map(u => (
                   <KartuBaris key={u.id} testid={`inv-unit-${u.identifier}-m`} onClick={() => lihatRiwayat(u.identifier)}
                               className={u.status === "terjual" ? "bg-rose-50/70" : ""}>
                     <KartuBarisHeader
                       kiri={<>
                         <p className="font-mono text-xs text-emerald-800">{u.identifier}</p>
                         <p className="text-sm font-semibold text-slate-900">{u.nama}</p>
                         <p className="text-xs text-slate-500 mt-0.5">{u.warehouse_nama}</p>
                       </>}
                       kanan={<StatusUnitBadge status={u.status} />}
                     />
                     <KartuBarisMeta label="Masuk" value={u.tanggal_masuk} />
                     {u.status === "terjual" && <KartuBarisMeta label="Terjual" value={u.tanggal_terjual || "—"} />}
                     {units.modal_terlihat && u.status === "terjual" && (
                       <KartuBarisMeta label="Harga Jual" value={idr(u.harga_jual)} tebal />
                     )}
                     {units.modal_terlihat && <KartuBarisMeta label="Modal" value={idr(u.harga_perolehan)} tebal />}
                   </KartuBaris>
                 ))} />
        </div>
      )}

      {!loading && tab === "transfer" && (
        <div className="space-y-3">
          {can("inventory_transfer", "create") && gudang.length >= 2 && (
            <button onClick={() => setFormTransfer({
              tanggal: hariIni(), dari_warehouse_id: gudang[0].id,
              ke_warehouse_id: gudang[1].id, catatan: "",
              items: [{ sku: "", qty: 1, unit_ids: [] }],
            })} data-testid="inv-transfer-add"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Transfer Barang
            </button>
          )}
          {gudang.length < 2 && (
            <p className="text-sm text-slate-500">Transfer butuh minimal dua gudang.</p>
          )}
          <Tabel kolom={["Kode / Tanggal", "Dari", "Ke", "Qty", "Nilai", "Status", "Oleh", "Aksi"]} kanan={[3, 4, 7]}
                 kosong="Belum ada perpindahan barang."
                 kartuTestid="inv-transfer-kartu"
                 baris={transfers.map(t => ({
                   key: t.id, testid: `inv-transfer-${t.id}`,
                   sel: [
                     <span><span className="font-mono text-[11px] text-slate-500 block">{t.code}</span>{t.tanggal}</span>,
                     t.dari_warehouse_nama, t.ke_warehouse_nama, t.qty_total,
                     <span className="font-mono">{idr(t.nilai_total)}</span>,
                     <StatusTransferBadge status={t.status} legacy={t.status_legacy} lewatiKirim={t.lewati_kirim} />,
                     t.created_by_name,
                     <div className="flex items-center justify-end gap-1">
                       <AksiTahapTransfer t={t} bolehUpdate={bolehTransferUpdate}
                                          onKirim={kirimTransfer} onTerima={terimaTransfer} onBatal={batalkanTransfer} />
                       <button onClick={() => bukaCetakTransfer(t)} data-testid={`btn-cetak-transfer-${t.id}`}
                               title="Cetak Surat Jalan"
                               className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                         <Printer className="w-4 h-4" />
                       </button>
                       {/* Bukti fisik barang dikirim & diterima — yang dicari
                           saat jumlah di dua gudang tidak cocok. */}
                       <button onClick={() => setLampiranTransfer(t)} data-testid={`inv-transfer-lampiran-${t.id}`}
                               title="Lampiran bukti kirim/terima"
                               className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                         <Paperclip className="w-4 h-4" />
                       </button>
                     </div>,
                   ],
                 }))}
                 kartu={transfers.map(t => (
                   <KartuBaris key={t.id} testid={`inv-transfer-${t.id}-m`}>
                     <KartuBarisHeader
                       kiri={<>
                         <p className="font-mono text-[11px] text-slate-500">{t.code}</p>
                         <p className="text-sm font-semibold text-slate-900">{t.dari_warehouse_nama} → {t.ke_warehouse_nama}</p>
                         <p className="text-xs text-slate-500 mt-0.5">{t.tanggal}</p>
                       </>}
                       kanan={<>
                         <span className="font-mono font-semibold text-slate-900 block">{idr(t.nilai_total)}</span>
                         <span className="block mt-1"><StatusTransferBadge status={t.status} legacy={t.status_legacy} lewatiKirim={t.lewati_kirim} /></span>
                       </>}
                     />
                     <KartuBarisMeta label="Qty" value={t.qty_total} />
                     <KartuBarisMeta label="Oleh" value={t.created_by_name} />
                     <KartuBarisAksi>
                       <AksiTahapTransfer t={t} bolehUpdate={bolehTransferUpdate} kartu
                                          onKirim={kirimTransfer} onTerima={terimaTransfer} onBatal={batalkanTransfer} />
                       <button onClick={() => bukaCetakTransfer(t)} data-testid={`btn-cetak-transfer-${t.id}-m`}
                               title="Cetak Surat Jalan"
                               className="px-2.5 h-8 rounded hover:bg-slate-100 text-slate-600 text-xs font-medium inline-flex items-center gap-1">
                         <Printer className="w-3.5 h-3.5" /> Cetak
                       </button>
                     </KartuBarisAksi>
                   </KartuBaris>
                 ))} />
          <p className="text-xs text-slate-500">
            Transfer tidak membuat jurnal — barang berpindah rak, bukan berpindah nilai. Modalnya ikut pindah
            supaya HPP saat terjual nanti tetap memakai harga perolehan aslinya. Stok ditarik dari gudang asal
            SEJAK 'Disiapkan' dan baru muncul di gudang tujuan setelah 'Diterima' — selama itu barang berstatus
            transit dan tidak bisa dijual dari gudang mana pun.
          </p>
        </div>
      )}

      {!loading && tab === "lembar" && <LembarPerhitungan gudang={gudang} />}

      {!loading && tab === "cutover" && rekon && (
        <div className="space-y-4">
          <div className={`border rounded-xl p-4 text-sm flex items-start gap-2 ${rekon.seimbang ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
               data-testid="inv-cutover-status">
            {rekon.seimbang ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            <div>
              {rekon.seimbang
                ? <>Nilai stok sudah sama dengan saldo akun persediaan di buku besar.
                    {cfg?.arus_keluar_aktif
                      ? <> Arus keluar aktif sejak <strong>{cfg.cutoff_date}</strong>.</>
                      : " Arus keluar belum diaktifkan."}</>
                : <>Buku besar mencatat {idr(rekon.total_saldo_coa)} sementara stok yang terhitung
                    kuantitasnya {idr(rekon.total_nilai_ledger)} — selisih <strong>{idr(rekon.selisih_total)}</strong>.
                    {" "}{rekon.penjelasan}</>}
            </div>
          </div>

          <Tabel kolom={["Kategori", "Akun", "Qty Tercatat", "Nilai Stok", "Saldo Buku Besar", "Selisih"]}
                 kanan={[2, 3, 4, 5]} kosong="—"
                 kartuTestid="inv-rekon-kartu"
                 baris={rekon.baris.map(b => ({
                   key: b.akun_code, testid: `inv-rekon-${b.akun_code}`,
                   sel: [
                     b.kategori, `${b.akun_code} ${b.akun_nama}`, b.qty_ledger,
                     <span className="font-mono">{idr(b.nilai_ledger)}</span>,
                     <span className="font-mono">{idr(b.saldo_coa)}</span>,
                     <span className={`font-mono ${Math.abs(b.selisih) > 0.01 ? "text-rose-700 font-semibold" : "text-slate-500"}`}>{idr(b.selisih)}</span>,
                   ],
                 }))}
                 kartu={rekon.baris.map(b => (
                   <KartuBaris key={b.akun_code} testid={`inv-rekon-${b.akun_code}-m`}>
                     <KartuBarisHeader
                       kiri={<>
                         <p className="text-sm font-semibold text-slate-900">{labelKategori(b.kategori)}</p>
                         <p className="text-xs text-slate-500 mt-0.5">{b.akun_code} {b.akun_nama}</p>
                       </>}
                       kanan={<span className={`font-mono ${Math.abs(b.selisih) > 0.01 ? "text-rose-700 font-semibold" : "text-slate-500"}`}>{idr(b.selisih)}</span>}
                     />
                     <KartuBarisMeta label="Qty Tercatat" value={b.qty_ledger} />
                     <KartuBarisMeta label="Nilai Stok" value={idr(b.nilai_ledger)} />
                     <KartuBarisMeta label="Saldo Buku Besar" value={idr(b.saldo_coa)} tebal />
                   </KartuBaris>
                 ))} />

          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-slate-900">Urutan mengaktifkan arus keluar</p>
            <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
              <li>Hitung fisik seluruh barang, masukkan sebagai Saldo Awal per gudang.</li>
              <li>Jalankan Penyesuaian COA — selisihnya dibukukan ke Laba Ditahan, bukan beban bulan ini.</li>
              <li>Aktifkan arus keluar dengan tanggal mulai. Transaksi sebelum tanggal itu tidak mengurangi stok.</li>
            </ol>
            <p className="text-xs text-amber-700">
              Selama langkah ini belum tuntas, penjualan belum mengurangi stok — disengaja, supaya stok
              yang fisiknya nyata ada tidak langsung minus.
            </p>
            {/* Kedua aksi ini memakai izin inventory_opname (create), bukan inventory_laporan
                yang menggerbangi tab ini. Menampilkannya ke user tanpa izin cuma menghasilkan
                403 setelah dia mengetik tanggal cutoff. */}
            {can("inventory_opname", "create") ? (
              <div className="flex gap-2 pt-1">
                <button onClick={() => kirim(() => api.post("/inventory/cutover/samakan-coa", {}), "Penyesuaian dibuat")}
                        disabled={busy || rekon.seimbang} data-testid="inv-cutover-samakan"
                        className="h-9 px-3 rounded-lg border border-slate-200 text-sm disabled:opacity-40">
                  Penyesuaian COA
                </button>
                <button onClick={() => {
                  const t = window.prompt("Arus keluar berlaku mulai tanggal (YYYY-MM-DD):", hariIni());
                  if (t) kirim(() => api.post("/inventory/cutover/aktifkan", { cutoff_date: t }), "Arus keluar aktif");
                }} disabled={busy || !rekon.seimbang || cfg?.arus_keluar_aktif} data-testid="inv-cutover-aktifkan"
                        className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-40">
                  Aktifkan Arus Keluar
                </button>
              </div>
            ) : (
              <p className="text-xs text-slate-500 pt-1">
                Hanya pemegang izin Opname Stok yang boleh menjalankan penyesuaian dan mengaktifkan arus keluar.
              </p>
            )}
          </div>
        </div>
      )}

      {riwayat && <ModalRiwayat data={riwayat} onClose={() => setRiwayat(null)} />}

      {formGudang && (
        <ModalGudang form={formGudang} setForm={setFormGudang} busy={busy}
                     onSimpan={async () => {
                       // Satu modal untuk buat & ubah: ada id = PUT, tanpa id = POST.
                       const ok = await kirim(
                         () => formGudang.id
                           ? api.put(`/inventory/warehouses/${formGudang.id}`, formGudang)
                           : api.post("/inventory/warehouses", formGudang),
                         formGudang.id ? "Gudang diperbarui" : "Gudang dibuat");
                       if (ok) setFormGudang(null);
                     }} />
      )}

      {formTransfer && (
        <ModalTransfer form={formTransfer} setForm={setFormTransfer} gudang={gudang} busy={busy}
                       onSimpan={async () => {
                         const body = {
                           ...formTransfer,
                           items: formTransfer.items.filter(i => i.sku).map(i => ({
                             sku: i.sku, qty: Number(i.qty) || 0, unit_ids: i.unit_ids || [],
                           })),
                         };
                         const ok = await kirim(() => api.post("/inventory/transfer", body), "Transfer disiapkan");
                         if (ok) setFormTransfer(null);
                       }} />
      )}

      {/* Cetak Surat Jalan pindah gudang (brief #18) */}
      {cetakTransfer && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setCetakTransfer(null)}>

      {lampiranTransfer && (
        <AttachmentPanel module="inventory_transfer" objId={lampiranTransfer.id}
          objLabel={`${lampiranTransfer.code || lampiranTransfer.id.slice(0, 8)}`}
          onClose={() => setLampiranTransfer(null)} />
      )}

          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="cetak-transfer-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Printer className="w-4 h-4" /> Cetak Surat Jalan
              </h3>
              <button onClick={() => setCetakTransfer(null)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {cetakTransfer.step === "pilih_template" ? (
                <div className="space-y-4">
                  <PemilihTemplate templates={cetakTransfer.templates} value={cetakTransfer.templateId}
                    onChange={(id) => setCetakTransfer(m => ({ ...m, templateId: id }))} jenisLabel="surat jalan pindah gudang"
                    testidPrefix="transfer-cetak-tpl" />
                  <div className="flex justify-end">
                    <button onClick={() => muatDokumenTransfer(cetakTransfer.transfer, cetakTransfer.templates.find(t => t.id === cetakTransfer.templateId))}
                      data-testid="transfer-cetak-tpl-lanjut"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                      Lanjutkan
                    </button>
                  </div>
                </div>
              ) : cetakTransfer.step === "kosong" ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="transfer-cetak-tpl-kosong">
                  Belum ada template surat jalan pindah gudang. Buat dulu di menu Template Faktur &amp; Struk.
                </p>
              ) : cetakTransfer.loading || !cetakTransfer.data ? (
                <LoadingState loading jumlahKartu={1} data-testid="cetak-transfer-loading" />
              ) : (
                <FakturPrint data={cetakTransfer.data} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Badge status unit ber-IMEI — "terjual" MERAH (permintaan owner, brief 12 Agu
// poin 1: barang yang sudah terjual harus jelas kelihatan), "tersedia" hijau
// seperti sebelumnya, status lain (transit/rusak/retur/hilang) abu-abu netral.
function StatusUnitBadge({ status }) {
  const kelas = status === "tersedia"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "terjual"
      ? "bg-rose-100 text-rose-700 border-rose-300"
      : "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`text-[11px] px-2 py-0.5 rounded border ${kelas}`}>{status}</span>;
}

// Badge status TAHAPAN transfer (brief owner 14 Agu poin 6). Nilai tak dikenal
// (harusnya tidak pernah terjadi — backend selalu mengisi salah satu dari
// TRANSFER_STATUSES atau default "diterima" untuk transfer lama) jatuh ke
// styling status "diterima" supaya badge tetap tampil rapi, bukan kosong/rusak.
function StatusTransferBadge({ status, legacy, lewatiKirim }) {
  const cfg = STATUS_TRANSFER[status] || STATUS_TRANSFER.diterima;
  // Transfer dari Request Stok (brief 4 Sep 2026 P17) tidak punya tahap Kirim:
  // 'disiapkan' di sini berarti "menunggu diterima di toko tujuan", bukan
  // "menunggu dikirim" — labelnya harus mengatakan itu, bukan menebak.
  const label = lewatiKirim && status === "disiapkan" ? "Menunggu diterima" : cfg.l;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded border ${cfg.c}`}
          data-testid={lewatiKirim && status === "disiapkan" ? "inv-transfer-badge-menunggu-diterima" : undefined}>
      {label}{legacy ? " (lama)" : ""}
    </span>
  );
}

// Tombol tahap lanjutan transfer — hanya tampil kalau statusnya benar-benar
// menuntut aksi itu DAN pemakai punya izin `inventory_transfer.update` (kalau
// tidak, tombolnya disembunyikan sama sekali, bukan ditampilkan lalu 403 saat
// diklik). Transfer 'diterima'/'dibatalkan' final — tidak ada tombol lagi.
function AksiTahapTransfer({ t, bolehUpdate, onKirim, onTerima, onBatal, kartu }) {
  if (!bolehUpdate || t.status === "diterima" || t.status === "dibatalkan") return null;
  const sufiks = kartu ? "-m" : "";
  const cls = "h-8 px-2.5 rounded text-xs font-medium inline-flex items-center gap-1";
  // Transfer dari Request Stok (P17): backend MENOLAK 'Kirim' (409) untuk
  // transfer ber-`lewati_kirim` — tombolnya tidak ditampilkan (aturan #3),
  // langsung Terima + Batalkan dari 'disiapkan'.
  if (t.status === "disiapkan" && t.lewati_kirim) {
    return (
      <>
        <button onClick={() => onTerima(t)} data-testid={`inv-transfer-terima-${t.id}${sufiks}`}
                title={`Dari Request Stok ${t.source_request_code || ""} — tanpa tahap Kirim`}
                className={`${cls} bg-emerald-600 hover:bg-emerald-700 text-white`}>
          <PackageCheck className="w-3.5 h-3.5" /> Terima
        </button>
        <button onClick={() => onBatal(t)} data-testid={`inv-transfer-batal-${t.id}${sufiks}`}
                className={`${cls} border border-rose-200 text-rose-700 hover:bg-rose-50`}>
          <Ban className="w-3.5 h-3.5" /> Batalkan
        </button>
      </>
    );
  }
  if (t.status === "disiapkan") {
    return (
      <>
        <button onClick={() => onKirim(t)} data-testid={`inv-transfer-kirim-${t.id}${sufiks}`}
                className={`${cls} bg-sky-600 hover:bg-sky-700 text-white`}>
          <Send className="w-3.5 h-3.5" /> Kirim
        </button>
        <button onClick={() => onBatal(t)} data-testid={`inv-transfer-batal-${t.id}${sufiks}`}
                className={`${cls} border border-rose-200 text-rose-700 hover:bg-rose-50`}>
          <Ban className="w-3.5 h-3.5" /> Batalkan
        </button>
      </>
    );
  }
  if (t.status === "dikirim") {
    return (
      <>
        <button onClick={() => onTerima(t)} data-testid={`inv-transfer-terima-${t.id}${sufiks}`}
                className={`${cls} bg-emerald-600 hover:bg-emerald-700 text-white`}>
          <PackageCheck className="w-3.5 h-3.5" /> Terima
        </button>
        <button onClick={() => onBatal(t)} data-testid={`inv-transfer-batal-${t.id}${sufiks}`}
                className={`${cls} border border-rose-200 text-rose-700 hover:bg-rose-50`}>
          <Ban className="w-3.5 h-3.5" /> Batalkan
        </button>
      </>
    );
  }
  return null;
}

function Kartu({ label, nilai, sub, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-xl font-bold mt-1.5 ${tone || "text-slate-900"}`}>{nilai}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>
    </div>
  );
}

// `kartu` (opsional): array node <KartuBaris> siap pakai, satu per baris,
// dibangun pemanggil dari SUMBER DATA yang sama dengan `baris` — testid kartu
// harus beda dari testid baris tabel (konvensi akhiran `-m`, lihat
// responsive-table.jsx) supaya query getByTestId tidak menabrak dua node.
//
// `kolom` item boleh berupa string biasa (header statis, dipakai tab
// Gudang/Unit/Transfer/Cutover) ATAU objek `{ key, label, testid }` untuk
// header yang bisa diklik untuk mengurutkan (dipakai tab Posisi Stok, brief
// owner 19 Agu poin 3) — kalau ada objek, `sortKey`/`sortDir`/`onSort` WAJIB
// disediakan pemanggil.
function Tabel({ kolom, baris, kanan = [], kosong, kartu, kartuTestid, sortKey, sortDir, onSort }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <TabelDesktop>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr className="text-left">
              {kolom.map((k, i) => (
                typeof k === "object"
                  ? <SortTh key={k.key} label={k.label} sortKeyName={k.key} sortKey={sortKey} sortDir={sortDir}
                            onSort={onSort} align={kanan.includes(i) ? "right" : "left"} testid={k.testid} />
                  : <th key={k} className={`px-3 py-2.5 font-semibold ${kanan.includes(i) ? "text-right" : ""}`}>{k}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {baris.length === 0 ? (
              <tr><td colSpan={kolom.length} className="px-4 py-10 text-center text-slate-400">{kosong}</td></tr>
            ) : baris.map(b => (
              <tr key={b.key} data-testid={b.testid} onClick={b.onClick}
                  className={`hover:bg-slate-50/60 ${b.onClick ? "cursor-pointer" : ""} ${b.kelas || ""}`}>
                {b.sel.map((s, i) => (
                  <td key={i} className={`px-3 py-2.5 ${kanan.includes(i) ? "text-right" : ""}`}>{s}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </TabelDesktop>

      {kartu && (
        <DaftarKartuMobile testid={kartuTestid}>
          {baris.length === 0 ? (
            <p className="px-4 py-10 text-center text-slate-400 text-sm">{kosong}</p>
          ) : kartu}
        </DaftarKartuMobile>
      )}
    </div>
  );
}

function Bingkai({ judul, children, onClose, onSimpan, busy, label, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid={testid}
           className="bg-white rounded-2xl max-w-lg w-full max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{judul}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        {onSimpan && (
          <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
            <button onClick={onSimpan} disabled={busy} data-testid={`${testid}-submit`}
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
              {busy ? "Menyimpan..." : label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalGudang({ form, setForm, busy, onSimpan }) {
  const judul = form.id ? `Ubah Gudang — ${form.nama || form.code}` : "Gudang Baru";
  // Daftar cabang terdaftar (permintaan bos): ditarik dari data HRIS lewat
  // backend, bukan ketikan bebas — "UP.Gadget" salah ketik tidak akan pernah
  // jadi cabang hantu yang membuat aturan akses gudang bolong.
  const [cabangOpsi, setCabangOpsi] = useState([]);
  useEffect(() => {
    let batal = false;
    api.get("/inventory/cabang-options")
      .then(r => { if (!batal) setCabangOpsi(r.data.items || []); })
      .catch(() => {});
    return () => { batal = true; };
  }, []);
  const toggleCabang = (c) => setForm(f => ({
    ...f,
    allowed_branches: (f.allowed_branches || []).includes(c)
      ? f.allowed_branches.filter(x => x !== c)
      : [...(f.allowed_branches || []), c],
  }));
  return (
    <Bingkai judul={judul} testid="inv-gudang-modal" onClose={() => setForm(null)}
             onSimpan={onSimpan} busy={busy} label="Simpan Gudang">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Nama gudang</label>
        <input value={form.nama} data-testid="inv-gudang-nama"
               onChange={e => setForm(f => ({ ...f, nama: e.target.value }))} className={inp} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Tipe</label>
          <select value={form.tipe} data-testid="inv-gudang-tipe"
                  onChange={e => setForm(f => ({ ...f, tipe: e.target.value }))} className={inp}>
            <option value="gudang">Gudang</option>
            <option value="toko">Toko</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Cabang lokasi</label>
          <select value={form.cabang || ""} data-testid="inv-gudang-cabang"
                  onChange={e => setForm(f => ({ ...f, cabang: e.target.value }))} className={inp}>
            <option value="">— pilih cabang —</option>
            {cabangOpsi.map(c => <option key={c} value={c}>{c}</option>)}
            {form.cabang && !cabangOpsi.includes(form.cabang) && (
              <option value={form.cabang}>{form.cabang} (lama)</option>
            )}
          </select>
        </div>
      </div>
      <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <input type="checkbox" checked={form.all_branches} data-testid="inv-gudang-semua"
               onChange={e => setForm(f => ({ ...f, all_branches: e.target.checked }))}
               className="w-4 h-4 rounded border-slate-300 mt-0.5" />
        <span>
          Boleh dipakai semua cabang
          <span className="block text-[11px] text-slate-500 mt-0.5">
            Kalau dimatikan, sebutkan cabang mana saja yang boleh memakainya — gudang tanpa aturan
            akses akan ditolak sistem.
          </span>
        </span>
      </label>
      {/* P15 (brief 4 Sep 2026, keputusan owner): gudang utama = pemasok
          request stok toko. Hanya bermakna untuk tipe Gudang — request stok
          memang cuma mencari pemasok bertipe gudang. */}
      {form.tipe === "gudang" && (
        <label className="flex items-start gap-2 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <input type="checkbox" checked={!!form.is_utama} data-testid="inv-gudang-utama"
                 onChange={e => setForm(f => ({ ...f, is_utama: e.target.checked }))}
                 className="w-4 h-4 rounded border-slate-300 mt-0.5" />
          <span>
            Gudang utama (pemasok request stok toko)
            <span className="block text-[11px] text-slate-500 mt-0.5">
              Request stok dari toko hanya melihat &amp; mengambil dari gudang bertanda ini. Kalau tidak ada
              satu pun yang ditandai, semua gudang bertipe Gudang dianggap pemasok (aturan lama).
            </span>
          </span>
        </label>
      )}
      {!form.all_branches && (
        <div data-testid="inv-gudang-allowed">
          <label className="block text-xs font-medium text-slate-600 mb-1">Cabang yang boleh memakai (bisa lebih dari satu)</label>
          <div className="border border-slate-200 rounded-lg p-3 grid grid-cols-2 gap-2">
            {cabangOpsi.map(c => (
              <label key={c} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={(form.allowed_branches || []).includes(c)}
                       onChange={() => toggleCabang(c)}
                       data-testid={`inv-gudang-allowed-${c}`}
                       className="w-4 h-4 rounded border-slate-300" />
                {c}
              </label>
            ))}
            {/* Cabang lama yang tidak ada lagi di daftar tetap ditampilkan agar
                bisa dicabut — kalau disembunyikan, aksesnya tersangkut selamanya. */}
            {(form.allowed_branches || []).filter(c => !cabangOpsi.includes(c)).map(c => (
              <label key={c} className="flex items-center gap-2 text-sm text-amber-700 cursor-pointer">
                <input type="checkbox" checked onChange={() => toggleCabang(c)}
                       className="w-4 h-4 rounded border-amber-300" />
                {c} (lama)
              </label>
            ))}
          </div>
          {(form.allowed_branches || []).length === 0 && (
            <p className="text-[11px] text-amber-700 mt-1">Centang minimal satu cabang — tanpa akses, gudang ini tidak bisa dipakai siapa pun.</p>
          )}
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Alamat</label>
        <input value={form.alamat} data-testid="inv-gudang-alamat"
               onChange={e => setForm(f => ({ ...f, alamat: e.target.value }))} className={inp} />
      </div>
    </Bingkai>
  );
}

function ModalTransfer({ form, setForm, gudang, busy, onSimpan }) {
  const [saran, setSaran] = useState([]);
  // Detail produk terpilih (has_imei dsb) — cuma diketahui SETELAH memilih dari saran
  // autocomplete, sama seperti pola BarisItem di InventoryPO.jsx.
  const [produk, setProduk] = useState(null);
  const [panelUnit, setPanelUnit] = useState(false);
  const [unitPilihan, setUnitPilihan] = useState([]);
  const [loadingUnit, setLoadingUnit] = useState(false);

  const item = form.items[0];
  const berImei = !!produk?.has_imei || (item.unit_ids && item.unit_ids.length > 0);

  const cari = async (teks) => {
    setForm(f => ({ ...f, items: [{ ...f.items[0], sku: teks.toUpperCase(), unit_ids: [] }] }));
    setProduk(null);
    setPanelUnit(false);
    if (teks.length < 2) return setSaran([]);
    try {
      const r = await api.get("/inventory/products/lookup", { params: { q: teks } });
      setSaran(r.data.items || []);
    } catch { setSaran([]); }
  };

  const pilih = (p) => {
    setForm(f => ({ ...f, items: [{ ...f.items[0], sku: p.sku, qty: p.has_imei ? 0 : 1, unit_ids: [] }] }));
    setProduk(p);
    setSaran([]);
    setPanelUnit(false);
  };

  // Unit-unit yang sudah tercentang mengacu ke gudang ASAL — ganti gudang asal berarti
  // pilihan lama tidak lagi berlaku (unit itu belum tentu ada di gudang yang baru).
  const dariSebelumnya = useRef(form.dari_warehouse_id);
  useEffect(() => {
    if (dariSebelumnya.current !== form.dari_warehouse_id) {
      dariSebelumnya.current = form.dari_warehouse_id;
      if ((item.unit_ids || []).length) {
        setForm(f => ({ ...f, items: [{ ...f.items[0], unit_ids: [], qty: berImei ? 0 : f.items[0].qty }] }));
      }
      setPanelUnit(false);
      setUnitPilihan([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.dari_warehouse_id]);

  const bukaPanelUnit = async () => {
    const akanTerbuka = !panelUnit;
    setPanelUnit(akanTerbuka);
    if (!akanTerbuka || !item.sku || !form.dari_warehouse_id) return;
    setLoadingUnit(true);
    try {
      const r = await api.get("/inventory/transfer/pilihan-unit", {
        params: { sku: item.sku, warehouse_id: form.dari_warehouse_id },
      });
      setUnitPilihan(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar unit ber-IMEI");
      setUnitPilihan([]);
    } finally { setLoadingUnit(false); }
  };

  const toggleUnit = (id) => setForm(f => {
    const cur = f.items[0].unit_ids || [];
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    return { ...f, items: [{ ...f.items[0], unit_ids: next, qty: next.length }] };
  });

  return (
    <Bingkai judul="Transfer Antar Gudang" testid="inv-transfer-modal" onClose={() => setForm(null)}
             onSimpan={onSimpan} busy={busy} label="Siapkan Transfer">
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
        Stok langsung ditarik dari gudang asal begitu disiapkan. Lanjutkan "Kirim" lalu "Terima" dari
        tab Transfer untuk menyelesaikan perpindahan ke gudang tujuan.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Dari gudang</label>
          <select value={form.dari_warehouse_id} data-testid="inv-transfer-dari"
                  onChange={e => setForm(f => ({ ...f, dari_warehouse_id: e.target.value }))} className={inp}>
            {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Ke gudang</label>
          <select value={form.ke_warehouse_id} data-testid="inv-transfer-ke"
                  onChange={e => setForm(f => ({ ...f, ke_warehouse_id: e.target.value }))} className={inp}>
            {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Tanggal</label>
        <DatePicker value={form.tanggal} data-testid="inv-transfer-tanggal"
               onChange={v => setForm(f => ({ ...f, tanggal: v }))} />
      </div>
      <div className="relative">
        <label className="block text-xs font-medium text-slate-600 mb-1">Barang</label>
        <input value={item.sku} onChange={e => cari(e.target.value)}
               placeholder="Ketik SKU atau nama" data-testid="inv-transfer-sku" className={inp} />
        {saran.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {saran.map(p => (
              <button key={p.sku} onClick={() => pilih(p)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                <span className="font-mono text-xs text-slate-500">{p.sku}</span> · {p.nama}
                {p.has_imei && <span className="ml-1 text-[10px] text-emerald-700">IMEI</span>}
                <span className="block text-[11px] text-slate-400">stok {p.stock_qty}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {berImei ? (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Unit ber-IMEI</label>
          <button type="button" onClick={bukaPanelUnit} data-testid="tf-pilih-imei-0"
                  className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Pilih IMEI ({(item.unit_ids || []).length})
          </button>
          {!form.dari_warehouse_id && (
            <p className="text-[11px] text-amber-700 mt-1">Pilih gudang asal dulu supaya daftar unit bisa dimuat.</p>
          )}
          {panelUnit && (
            <div className="mt-2 border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-100">
              {loadingUnit ? (
                <div className="p-2"><LoadingState loading jumlahKartu={1} data-testid="tf-unit-loading" /></div>
              ) : unitPilihan.length === 0 ? (
                <p className="p-3 text-xs text-slate-400 text-center">Tidak ada unit tersedia di gudang asal untuk SKU ini.</p>
              ) : unitPilihan.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={(item.unit_ids || []).includes(u.id)}
                         onChange={() => toggleUnit(u.id)}
                         data-testid={`tf-imei-${u.identifier}`} className="w-4 h-4 rounded border-slate-300" />
                  <span className="font-mono text-xs">{u.identifier}</span>
                  <span className="text-[11px] text-slate-500">masuk {u.tanggal_masuk}</span>
                </label>
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-1">Jumlah otomatis mengikuti banyaknya unit yang dicentang.</p>
        </div>
      ) : (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Jumlah</label>
          <input type="number" min="1" value={item.qty} data-testid="inv-transfer-qty"
                 onChange={e => setForm(f => ({ ...f, items: [{ ...f.items[0], qty: e.target.value }] }))}
                 className={inp} />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Catatan</label>
        <input value={form.catatan} data-testid="inv-transfer-catatan"
               onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))} className={inp} />
      </div>
    </Bingkai>
  );
}

function ModalRiwayat({ data, onClose }) {
  const u = data.unit || {};
  return (
    <Bingkai judul={`Riwayat ${u.identifier}`} testid="inv-riwayat-modal" onClose={onClose}>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
        <p className="font-semibold text-slate-900">{u.nama}</p>
        <p className="text-slate-600">
          {u.sku} · modal <span className="font-mono">{idr(u.harga_perolehan)}</span> ·
          status <strong>{u.status}</strong>
        </p>
        <p className="text-slate-600">Sekarang di: {data.ringkasan?.gudang_sekarang || "—"}</p>
        {data.asal && (
          <p className="text-[11px] text-slate-500">
            Masuk lewat {data.asal.code}{data.asal.po_code ? ` (PO ${data.asal.po_code})` : ""}
            {data.asal.supplier_nama ? ` dari ${data.asal.supplier_nama}` : ""} · {data.asal.tanggal}
          </p>
        )}
      </div>
      <div className="space-y-2">
        {(data.jejak || []).map((j, i) => (
          <div key={i} className="flex items-start gap-3 border-l-2 border-emerald-300 pl-3 py-1">
            <div className="min-w-0">
              <p className="text-sm text-slate-900">
                {j.jenis === "masuk" ? "Masuk ke" : "Keluar dari"} <strong>{j.gudang}</strong>
              </p>
              <p className="text-[11px] text-slate-500">
                {j.tanggal} · {j.ref_type} · {j.code}
              </p>
              {j.catatan && <p className="text-[11px] text-slate-500 truncate">{j.catatan}</p>}
              <p className="text-[11px] text-slate-500">
                oleh {j.oleh || "—"}
                {/* Backend MEMBUANG field ini sama sekali (bukan null) kalau pemanggil
                    tidak punya izin inventory_laporan.view — tampilkan "Rp 0" di sini
                    akan mengarang angka, jadi baris nilai cuma dirender saat memang ada. */}
                {j.nilai != null && <> · <span className="font-mono">{idr(j.nilai)}</span></>}
              </p>
            </div>
          </div>
        ))}
        {(data.jejak || []).length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">Belum ada pergerakan tercatat.</p>
        )}
      </div>
    </Bingkai>
  );
}

// ============ P11 & P12 (brief owner 4 Sep 2026) — Lembar Perhitungan Stok ============
// Kertas kerja opname: qty sistem TAMPIL, tujuh kolom hari sengaja KOSONG untuk
// ditulis tangan, centang hanya memilih baris yang ikut dicetak, dan tidak ada
// input balik ke sistem (keputusan owner). Berbeda arah dari Blind Opname yang
// justru menyembunyikan qty sistem — dua alat, dua tujuan.
function LembarPerhitungan({ gudang }) {
  const cabangOptions = useCabangOptions();
  const [f, setF] = useState({
    tanggal: hariIniISO(), warehouse_id: "", cabang: "", kategori: "", brand: "",
    kuantitas: "semua", q: "",
  });
  const [data, setData] = useState(null);
  const [pilih, setPilih] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [rincian, setRincian] = useState(null);
  const [opsi, setOpsi] = useState({ brand: [], kategori: [] });

  useEffect(() => {
    // Daftar brand & kategori diambil dari sumber yang sama dengan layar lain,
    // bukan dikarang dari data yang kebetulan tampil.
    api.get("/inventory/master-opsi")
      .then(r => setOpsi(o => ({ ...o, brand: r.data?.brand_gadget?.length ? r.data.brand_gadget : (r.data?.brand || []) })))
      .catch(() => {});
  }, []);

  const muat = async () => {
    setBusy(true);
    try {
      const r = await api.get("/inventory/lembar-perhitungan", { params: bersih(f) });
      setData(r.data);
      setPilih(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat lembar perhitungan");
    } finally { setBusy(false); }
  };

  // Parameter kosong TIDAK dikirim: string kosong akan lolos ke server sebagai
  // filter "cabang = ''" dan mengosongkan hasilnya tanpa alasan yang terlihat.
  const bersih = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => String(v || "").trim()));

  const semuaTercentang = !!data?.items?.length && pilih.size === data.items.length;
  const toggleSemua = () => setPilih(semuaTercentang ? new Set() : new Set((data?.items || []).map(b => b.sku)));
  const toggleSatu = (sku) => setPilih(s => {
    const n = new Set(s);
    if (n.has(sku)) n.delete(sku); else n.add(sku);
    return n;
  });

  const barisTercetak = (data?.items || []).filter(b => !pilih.size || pilih.has(b.sku));

  const unduh = async () => {
    try {
      const params = bersih(f);
      if (pilih.size) params.sku = [...pilih].join(",");
      const r = await api.get("/inventory/lembar-perhitungan/export-xlsx",
                              { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lembar-perhitungan-${f.tanggal || "hari-ini"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh berkas"); }
  };

  const bukaRincian = async (sku) => {
    try {
      const r = await api.get(`/inventory/lembar-perhitungan/${encodeURIComponent(sku)}/rincian`,
                              { params: bersih({ tanggal: f.tanggal, warehouse_id: f.warehouse_id, cabang: f.cabang }) });
      setRincian(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat rincian"); }
  };

  const HARI = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

  return (
    <div className="space-y-4" data-testid="inv-lembar">
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <p className="text-sm font-semibold text-slate-800 mb-1">Lembar Perhitungan Stok</p>
        <p className="text-xs text-slate-500 mb-3">
          Kertas kerja opname mingguan: qty sistem tercetak, tujuh kolom hari dikosongkan untuk
          ditulis tangan. Centang baris yang mau dicetak — tidak ada yang tersimpan ke sistem.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Tanggal</label>
            <DatePicker value={f.tanggal} onChange={v => setF({ ...f, tanggal: v })} data-testid="lembar-tanggal" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Gudang</label>
            <select value={f.warehouse_id} onChange={e => setF({ ...f, warehouse_id: e.target.value })}
                    className={inp} data-testid="lembar-gudang">
              <option value="">Semua gudang</option>
              {(gudang || []).map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Cabang</label>
            <select value={f.cabang} onChange={e => setF({ ...f, cabang: e.target.value })}
                    className={inp} data-testid="lembar-cabang">
              <option value="">Semua cabang</option>
              {cabangOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Kategori</label>
            <select value={f.kategori} onChange={e => setF({ ...f, kategori: e.target.value })}
                    className={inp} data-testid="lembar-kategori">
              <option value="">Semua kategori</option>
              {(data?.items || []).length === 0 && !f.kategori ? null : null}
              {["handphone_baru", "handphone_second", "tab_baru", "tab_second", "laptop_baru",
                "laptop_second", "wearable_baru", "wearable_second", "original_acc",
                "accessories", "sim_sticker"].map(k => (
                <option key={k} value={k}>{labelKategori(k)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Brand</label>
            <select value={f.brand} onChange={e => setF({ ...f, brand: e.target.value })}
                    className={inp} data-testid="lembar-brand">
              <option value="">Semua brand</option>
              {(opsi.brand || []).map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Kuantitas</label>
            <select value={f.kuantitas} onChange={e => setF({ ...f, kuantitas: e.target.value })}
                    className={inp} data-testid="lembar-kuantitas">
              <option value="semua">Semua</option>
              <option value="kosong">Sama dengan 0</option>
              <option value="ada">Lebih besar dari 0</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Cari SKU/nama</label>
            <input value={f.q} onChange={e => setF({ ...f, q: e.target.value })}
                   className={inp} placeholder="opsional" data-testid="lembar-q" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={muat} disabled={busy} data-testid="lembar-tampilkan"
                  className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Memuat..." : "Tampilkan"}
          </button>
          {data && (
            <>
              <button onClick={() => window.print()} data-testid="lembar-cetak"
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1.5">
                <Printer className="w-4 h-4" /> Cetak ({barisTercetak.length} baris)
              </button>
              <button onClick={unduh} data-testid="lembar-export"
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1.5">
                <Download className="w-4 h-4" /> Export Excel
              </button>
            </>
          )}
        </div>
      </div>

      {data?.catatan && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800"
             data-testid="lembar-catatan">{data.catatan}</div>
      )}
      {data?.terpotong && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800"
             data-testid="lembar-terpotong">
          Daftar dipotong di {data.count} baris. Persempit filternya supaya tidak ada yang tertinggal
          saat dihitung.
        </div>
      )}

      {data && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto print-area">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-2 w-10 print:hidden">
                  <input type="checkbox" checked={semuaTercentang} onChange={toggleSemua}
                         data-testid="lembar-centang-semua" aria-label="Centang semua baris" />
                </th>
                <th className="p-2 text-left">SKU</th>
                <th className="p-2 text-left">Nama Produk</th>
                <th className="p-2 text-right">QTY</th>
                {HARI.map(h => <th key={h} className="p-2 w-12 text-center text-slate-400">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {barisTercetak.map(b => (
                <tr key={b.sku} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-2 print:hidden">
                    <input type="checkbox" checked={pilih.has(b.sku)} onChange={() => toggleSatu(b.sku)}
                           data-testid={`lembar-centang-${b.sku}`} aria-label={`Centang ${b.sku}`} />
                  </td>
                  <td className="p-2 font-mono text-xs">
                    <button onClick={() => bukaRincian(b.sku)} data-testid={`lembar-baris-${b.sku}`}
                            className="text-emerald-700 hover:underline">{b.sku}</button>
                  </td>
                  <td className="p-2">{b.nama}</td>
                  <td className="p-2 text-right font-semibold">{b.qty}</td>
                  {/* Tujuh kolom hari SENGAJA kosong — diisi tangan di rak. */}
                  {HARI.map(h => <td key={h} className="p-2 border-l border-slate-100">&nbsp;</td>)}
                </tr>
              ))}
              {barisTercetak.length === 0 && (
                <tr><td colSpan={11} className="p-6 text-center text-slate-400">
                  Tidak ada baris untuk filter ini.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {rincian && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
             onClick={() => setRincian(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="lembar-rincian"
               className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">{rincian.produk?.nama}</p>
                <p className="text-xs text-slate-500 font-mono">
                  {rincian.produk?.sku} · QTY {rincian.qty} · {rincian.count} unit ber-IMEI
                </p>
              </div>
              <button onClick={() => setRincian(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-2 text-left">IMEI / SN</th>
                    <th className="p-2 text-left">Gudang</th>
                    <th className="p-2 text-left">Status</th>
                    {rincian.modal_terlihat && <th className="p-2 text-right">Harga Beli</th>}
                    <th className="p-2 text-left">Catatan dari PO</th>
                  </tr>
                </thead>
                <tbody>
                  {rincian.items.map(u => (
                    <tr key={u.identifier} className="border-b border-slate-100">
                      <td className="p-2 font-mono text-xs">{u.identifier}</td>
                      <td className="p-2">{u.gudang}</td>
                      <td className="p-2">{u.status}</td>
                      {rincian.modal_terlihat && <td className="p-2 text-right">{idr(u.harga_beli || 0)}</td>}
                      <td className="p-2 text-slate-600">{u.catatan || "-"}</td>
                    </tr>
                  ))}
                  {rincian.items.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-400">
                      Produk ini tidak punya unit ber-IMEI di filter tersebut.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
